/**
 * Keyword filtering for investigation tab results (case-insensitive substring per term).
 * Supports explicit AND/OR between terms (left-associative).
 */

/** Empty query sentinel for React state */
export function emptyKeywordQuery() {
  return { terms: [], ops: [] };
}

/**
 * @param {*} input Legacy `string[]` (OR semantics) or `{ terms: string[], ops: ('AND'|'OR')[] }`
 */
export function normalizeKeywordQuery(input) {
  if (input == null) return { terms: [], ops: [] };
  if (Array.isArray(input)) {
    const terms = input.map((f) => String(f).trim()).filter(Boolean);
    const ops = [];
    for (let i = 0; i < Math.max(0, terms.length - 1); i += 1) ops.push('OR');
    return { terms, ops };
  }
  const rawTerms = Array.isArray(input.terms) ? input.terms : [];
  const terms = rawTerms.map((t) => String(t).trim()).filter(Boolean);
  let ops = Array.isArray(input.ops) ? [...input.ops] : [];
  const need = Math.max(0, terms.length - 1);
  while (ops.length < need) ops.push('AND');
  ops = ops.slice(0, need);
  for (let i = 0; i < ops.length; i += 1) {
    ops[i] = ops[i] === 'OR' ? 'OR' : 'AND';
  }
  return { terms, ops };
}

export function isKeywordQueryActive(queryInput) {
  const { terms } = normalizeKeywordQuery(queryInput);
  return terms.length > 0;
}

/** Single-term match helper */
export function substringMatchHay(hayLower, term) {
  const t = String(term).trim().toLowerCase();
  if (!t) return false;
  return hayLower.includes(t);
}

/**
 * Left-associate: ((((m(t0) op0 m(t1)) op1 m(t2))) …)
 */
export function textMatchesKeywordQuery(haystack, queryInput) {
  const { terms, ops } = normalizeKeywordQuery(queryInput);
  if (terms.length === 0) return true;
  const hay = String(haystack ?? '').toLowerCase();
  let r = substringMatchHay(hay, terms[0]);
  for (let i = 1; i < terms.length; i += 1) {
    const op = ops[i - 1] === 'OR' ? 'OR' : 'AND';
    const mi = substringMatchHay(hay, terms[i]);
    r = op === 'AND' ? r && mi : r || mi;
  }
  return r;
}

/**
 * Row matches if serialized text matches the keyword query (broad catch-all).
 */
export function objectMatchesKeywordFilters(obj, queryInput) {
  if (!isKeywordQueryActive(queryInput)) return true;
  try {
    return textMatchesKeywordQuery(JSON.stringify(obj), queryInput);
  } catch {
    return textMatchesKeywordQuery(String(obj), queryInput);
  }
}

/** Legacy alias — now supports AND/OR via `normalizeKeywordQuery`. */
export function textMatchesKeywordFilters(haystack, filters) {
  return textMatchesKeywordQuery(haystack, filters);
}

/**
 * PublicWWW rows: keywords apply only to URL and Snippets.
 */
export function publicWwwUrlSnippetMatchesKeywordFilters(row, queryInput) {
  if (!isKeywordQueryActive(queryInput)) return true;
  const url = String(row?.informationValue ?? '');
  const snippet = String(row?.publicWwwSnippet ?? '');
  return textMatchesKeywordQuery(`${url}\n${snippet}`, queryInput);
}

/** UrlScan rows: PublicWWW subtype uses URL+snippet; others use JSON match. */
export function urlScanRowMatchesKeywordFilters(item, queryInput) {
  if (!isKeywordQueryActive(queryInput)) return true;
  if (item?.informationType === 'PUBLICWWW_WEBSITE') {
    return publicWwwUrlSnippetMatchesKeywordFilters(item, queryInput);
  }
  return objectMatchesKeywordFilters(item, queryInput);
}
